import { DBEntity } from "./DBEntity";
import { DbLine } from "./DbLine";
import { DbMesh } from "./DbMesh";
import {
  BufferGeometry,
  DbLayer,
  Matrix4,
  ModelManager,
  ViewManager,
} from "@/CadEngine";
import { MLayer } from "./MLayer";
import { Layer } from "@/vendor/protobuf/packet2d";
import { DrawOperationMovePointParams } from "@/draws/IDrawOperation";

export enum CommonColor {
  Red = 0xff0000,
  Green = 0x008000,
  Gray = 0x808080,
  White = 0xffffff,
  Pick = 0x4addec,
  Aux = 0xf7d54a,
  EditAssistBase = 0x00b1ff,
  EditAssistActive = 0xa50000,
  Black = 0x000000,
  Background = 0x2b2d31,
  Blue = 0x0000ff,
  Mask = 0x212121,
  LightGreen = 0x00ff00,
}

export enum SampleLayerType {
  Line = "1",
}
export enum Linearity {
  Continuous = "Continuous",
  Dash = "Dash",
}

export interface Selection {
  location: { x: number; y: number; z: number };
  entity: DBEntity | THREE.Object3D | undefined;
  code?: string;
  offset?: { x: number; y: number; z: number };
}

export interface InsertObject {
  lines: {
    [key: string]: {
      lines: DbLine[];
      model: THREE.LineSegments | THREE.Line;
      canEdit: boolean;
    };
  };
  meshs: {
    [key: string]: { meshs: DbMesh[]; canEdit: boolean; model: THREE.Mesh };
  };
}

export enum SelectionState {
  None = "none",
  Pick = "pick",
  SelectionBox = "selectionBox",
  Place = "place",
  drawline = "drawline",
}

export enum Selection_Event {
  Hover = "hover",
  Pick = "pick",
  SelectionBox = "selectBox",
  Place = "place",
  addhole = "addhole",
  drawline = "drawline",
  snapFilter = "snapFilter",
  inserts = "inserts",
}

export enum ActiveInputName {
  none = "none",
  distance = "distance",
  angle = "angle",
}

export interface DimensionsAnnotationModifyReturnData {
  linePoints: THREE.Vector3[];
  distance: string;
}

export interface EditModifyAcceptParams {
  stipulateLayerNames: string[];
}

export interface ExtendHandleParameter {
  referData: DbLine;
  transformDatas: DbLine[];
  isMerge: boolean;
}

export enum PipelineModifyState {
  UP,
  DOWN,
}

export enum ReversalModifyState {
  TOP,
  LEFT,
}

export interface SlopeAnnotationModifyReturnData {
  line: { start: THREE.Vector3; end: THREE.Vector3 };
  arrowPoints: THREE.Vector3[];
  directionVector: THREE.Vector3;
  selectObject: DbLine | undefined;
}

export interface DrawOperateLineParams {
  layer: DbLayer;
  model: ModelManager;
  auxLayer: MLayer;
  view: ViewManager;
  color: number;
}

export interface EllipseReturnData {
  center: {
    x: number;
    y: number;
    z: number;
  };
  longVector: THREE.Vector3;
  longLength: number;
  shortVector: THREE.Vector3;
  shortLength: number;
  id?: string;
}

export interface EllipseArcReturnData {
  center: {
    x: number;
    y: number;
    z: number;
  };
  longVector: THREE.Vector3;
  longLength: number;
  shortVector: THREE.Vector3;
  shortLength: number;
  startRadian: number;
  endRadian: number;
}

export enum ArraySignType {
  circle = "circle",
  rectangle = "rectangle",
}

export type DistanceInfo = {
  distance: number;
  xDistance: number;
  yDistance: number;
};

export enum ArcLayoutType {
  interval = "interval",
  quantity = "quantity",
}

export interface FinishResult {
  dblines: DBEntity[];
  matrixs: THREE.Matrix4[];
}
export interface FinishResultToArc {
  center: THREE.Vector3;
  points: THREE.Vector3[];
  matrixs: THREE.Matrix4[];
  isClockwise: boolean;
}

export enum LayoutType {
  PointLayout = "PointLayout",
  LineLayout = "LineLayout",
  RectangleLayout = "RectangleLayout",
  ArcLayout = "ArcLayout",
  FanShapedLayout = "FanShapedLayout",
}

export interface DrawLayoutParams {
  layer: DbLayer; //图层
  color: number;
  completeCallback: (parms: FinishResult | FinishResultToArc) => void; //绘制结束回调
  callbackUnderway: (point: THREE.Vector3) => void;
  layoutType: LayoutType; //绘制类型
  isPipeLink?: boolean; //是否是管线
  specialParams?: any;
}

export interface LinePoints {
  start: THREE.Vector3;
  end: THREE.Vector3;
}

export interface CircleArcInfoUseFilleted {
  center: THREE.Vector3;
  radius: number;
  start: THREE.Vector3;
  end: THREE.Vector3;
  clockwise: boolean;
  originData?: DbLine;
}

export interface ElevationAnnotationModifyReturnData {
  basePoint: THREE.Vector3[];
  dirPoint: THREE.Vector3 | undefined;
  dirState: number | undefined;
}

export interface DimensionsAnnotationContinuousModifyTempObjectData {
  textInfo: { text: string; textPosition: THREE.Vector3 };
  lineObj: THREE.LineSegments;
  linePoints: THREE.Vector3[];
  dropPoint: THREE.Vector3 | undefined;
}

export interface DimensionsAnnotationContinuousModifyReturnData {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  fallPoint: THREE.Vector3;
  points: THREE.Vector3[];
}

export interface MultiLineAnnotationModifyAcceptParams {
  textHeight: number;
  textLineNumber: number;
}

export interface MultiPipeAnnotationModifyReturnData {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  signMatrixs: THREE.Matrix4[];
}

export interface DrawCuttingSymbolHandleAcceptParams {
  callBackFinish: (result: any) => void;
  orthogonality: boolean;
}
export interface DrawCuttingSymbolHandleReturnData {
  points: THREE.Vector3[];
  dirPoint: THREE.Vector3;
}

export interface RotateModifyReturnData {
  basePoint: THREE.Vector3;
  rotateAngle: number;
}

export interface MoveModifyReturnData {
  moveVector: THREE.Vector3;
}
export interface CopyModifyReturnData {
  copyDatas: (DbLine | DbMesh)[];
  moveVector: THREE.Vector3;
}

export enum ActiveInputType {
  None = "none",
  Distance = "distance",
  Angle = "angle",
}

export interface AnnotationDrawnModifyAcceptParams {
  callBackFinish: (result: THREE.Vector3[]) => void;
  callbackUnderway: (point: THREE.Vector3) => void;
  basePointNum: number;
}

export interface AnnotationDrawnModifyReturnData {
  basePoints: THREE.Vector3[];
}

export interface BreakModifyReturnData {
  delData: DbLine[];
  addData: DbLine[];
  breakInfo: {
    breakPoints: THREE.Vector3[];
    extendId: string;
  }[];
}

export interface InsertFileAcceptParams {
  file: string;
  acceptParametersCallBack: (param: {
    insertData: DBEntity[];
    intersectModels: THREE.Intersection[];
    matrix: THREE.Matrix4;
    select: DBEntity | undefined;
  }) => Promise<any>;
  isInsertContinuous: boolean;
  scale: number | { x: number; y: number; z: number };
  isInsertManger?: Function;
  fitLayerName?: string[] | undefined;
  isProhibitionRotate?: boolean;
  isFitAndLibertyInsert?: boolean;
}

export interface InsertDatas {
  lines: {
    [key: string]: {
      lines: DbLine[];
      model: THREE.LineSegments | THREE.Line;
      canEdit: boolean;
    };
  };
  meshs: {
    [key: string]: { meshs: DbMesh[]; canEdit: boolean; model: THREE.Mesh };
  };
}

export interface InsertFilePassMatrixAcceptParams {
  file: string;
  callBackFinish: (param: InsertFilePassMatrixReturnData) => void;
  matrixs: Matrix4[];
}

export interface InsertFilePassMatrixReturnData {
  extendId: string[];
  insertDatas: (DbLine | DbMesh)[];
}

export interface DrawDoorOrWindowAcceptParams {
  wallLayerName: string[];
  wallCenterLayerName: string[];
  width: number;
  callBackFinish: (result: any) => void;
  callbackUnderway: (point: THREE.Vector3) => void;
}

export interface DrawDoorWayLineAcceptParams {
  doorLayerName: string;
  callBackFinish: (result: any) => void;
  callbackUnderway: (point: THREE.Vector3) => void;
}

export interface DrawDoorOrWindowReturnData {
  extendId: string | undefined;
  basePoint: THREE.Vector3 | undefined;
  dirPoint: THREE.Vector3 | undefined;
  thick: number;
}

export interface ReversalReturnData {
  reversalDatas: {
    extendId: string;
    reversalMatrix: THREE.Matrix4;
  }[];
  sourceDatas: (DbLine | DbMesh)[];
}
export enum drawWallType {
  drawLine = 0,
  drawCircleArc = 1,
  drawRectangle = 2,
}
export interface DrawWallAcceptParams {
  leftWallWidth: number;
  rightWallWidth: number;
  leftFaceLayer: number;
  rightFaceLayer: number;
  drawWallType: drawWallType;
  callBackFinish: (result: any) => void;
  callbackUnderway: (point: THREE.Vector3) => void;
}

// export interface DrawWallReturnData {
//   basePoint: THREE.Vector3[];
// }
export interface DrawLineParams {
  layer: DbLayer;
  color: number;
  callbackUnderway: (
    params: DrawOperationMovePointParams,
    points: THREE.Vector3[]
  ) => boolean;
}

export interface DrawDashLineParams {
  layer: DbLayer;
  color: number;
  callbackUnderway: (point: THREE.Vector3) => void;
}
export interface DrawWidthLineParams {
  layer: DbLayer;
  color: number;
  width: number;
  isDashed: boolean;
  callbackUnderway: (point: THREE.Vector3) => void;
}

export interface CircleArcInfo {
  center: THREE.Vector3;
  radius: number;
  startAngle: number;
  endAngle: number;
  clockwise: boolean;
}

export interface DrawWallRoadAcceptParams {
  leftWallWidth: number;
  rightWallWidth: number;
  leftFaceLayer: number;
  rightFaceLayer: number;
}

export interface LoopAnnotationModifyAcceptParams {
  text: string;
  number: string;
  isContinuous: boolean;
  isNumberContinuous: boolean;
  accordLayerNames: string[];
  callBackFinish: (result: any) => void;
  callbackUnderway: (point: THREE.Vector3) => void;
}

export interface LoopAnnotationModifyReturnData {
  signMatrix: THREE.Matrix4;
  linePoints: THREE.Vector3[];
  textPosition: THREE.Vector3;
  extendId: string;
}
export interface PolygonData {
  id: string;
  linePoints: THREE.Vector2[];
  shapeMesh: THREE.Mesh;
}

export interface InsertConnectPointModifyAcceptParams {
  layer: DbLayer;
  color: number;
  callBackFinish: (result: any) => void;
  callbackUnderway: (point: THREE.Vector3) => void;
}

export interface DimensionsWireCountModifyAcceptParams {
  wireCount: string;
  accordLayerNames: string[];
  callBackFinish: (result: any) => void;
  callbackUnderway: (point: THREE.Vector3) => void;
}

export interface DimensionsWireCountModifyReturnData {
  signMatrixs: THREE.Matrix4[];
  textPoints: THREE.Vector3[];
}
